import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar, Trash3, XClose } from "@hortis/ui/icons";
import { Input } from "@hortis/ui/input";
import { toast } from "@hortis/ui/sonner";
import { Textarea } from "@hortis/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { A, D, pipe } from "@mobily/ts-belt";
import { ReactNode } from "@tanstack/react-router";
import {
  AccessionFieldsFragment,
  namedOperations,
  useGetMeWithSitesQuery,
} from "generated/graphql";
import { useEffect, useMemo, useState } from "react";
import {
  type DeepPartial,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { queryClient } from "src/app";
import { FormSelect, type SelectOption } from "src/components/select";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { updateAccessionDocument } from "src/features/records/accession/graphql";
import { convertDatesToISO } from "src/utils/convert-dates-to-iso";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import { createFullName } from "src/utils/full-name";
import { useGqlMutation } from "src/utils/hooks/graphql";
import {
  IdentificationQualifier,
  IdentificationVerificationStatus,
  identificationQualifierLabelMap,
  identificationVerificationStatusMap,
} from "src/utils/label-maps/identification";
import { DiscardDialog } from "./discard-dialog";
import { RemoveDialog } from "./remove-dialog";
import {
  type AccessionDataZod,
  type IdentificationDetails,
  identificationDetailsSchema,
} from "./schema";

export const extractIdentificationFields = (
  accession: AccessionFieldsFragment,
) => ({
  identifiedBy: accession.identifiedBy ?? undefined,
  dateIdentified:
    accession.dateIdentified == null
      ? undefined
      : parseUtcDate(accession.dateIdentified),
  identificationVerificationStatus:
    (accession.identificationVerificationStatus as IdentificationVerificationStatus) ??
    undefined,
  identificationQualifier:
    (accession.identificationQualifier as IdentificationQualifier) ?? undefined,
  identificationRemarks: accession.identificationRemarks ?? undefined,
});

export const useIdentificationFieldCount = () => {
  const form = useFormContext<DeepPartial<AccessionDataZod>>();
  const identificationDetails = form.watch("identificationDetails");
  const identifiedBy = form.watch("identificationDetails.identifiedBy");
  const dateIdentified = form.watch("identificationDetails.dateIdentified");
  const identificationVerificationStatus = form.watch(
    "identificationDetails.identificationVerificationStatus",
  );
  const identificationQualifier = form.watch(
    "identificationDetails.identificationQualifier",
  );
  const identificationRemarks = form.watch(
    "identificationDetails.identificationRemarks",
  );
  if (identificationDetails == null) {
    return 0;
  }
  let fieldCount = 0;
  if (identifiedBy != null && identifiedBy !== "") {
    fieldCount++;
  }
  if (dateIdentified != null) {
    fieldCount++;
  }
  if (identificationVerificationStatus != null) {
    fieldCount++;
  }
  if (identificationQualifier != null) {
    fieldCount++;
  }
  if (identificationRemarks != null && identificationRemarks !== "") {
    fieldCount++;
  }
  return fieldCount;
};

export const IdentificationDialog = ({
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  initialData,
  onSubmit,
  children,
  accessionId,
  onSuccess,
}: {
  initialData?: Partial<IdentificationDetails>;
  onSubmit?: (data: IdentificationDetails | null | undefined) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
  accessionId?: string;
  onSuccess?: () => void;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [openInternal, onOpenChangeInternal] = useState(
    openControlled ?? false,
  );
  const open = openControlled ?? openInternal;
  const onOpenChange = onOpenChangeControlled ?? onOpenChangeInternal;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [{ data }] = useGetMeWithSitesQuery();
  const me = data?.me;
  const defaultValues = useMemo(
    () =>
      initialData ?? {
        identifiedBy:
          me == null ? undefined : (createFullName(me) ?? undefined),
        dateIdentified: new Date(),
        identificationVerificationStatus: undefined,
        identificationQualifier: null,
        identificationRemarks: null,
      },
    [initialData, me],
  );
  const form = useForm<Partial<IdentificationDetails>>({
    defaultValues,
    resolver: zodResolver(identificationDetailsSchema),
  });

  const updateAccession = useGqlMutation({
    mutation: updateAccessionDocument,
    onError: () => {
      toast.error("Failed to update identification details");
    },
    onSuccess: (res, variables) => {
      if (!res.updateAccession.success) {
        toast.error("Failed to update identification details");
      }
      const isRemove =
        variables.updates.identificationVerificationStatus == null &&
        variables.updates.identifiedBy == null &&
        variables.updates.identificationQualifier == null &&
        variables.updates.identificationRemarks == null &&
        variables.updates.dateIdentified == null;
      toast.success(
        `Identification details ${isRemove ? "removed" : "updated"}`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccession],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccessionById],
      });
      onOpenChange(false);
      onSuccess?.();
      form.reset(defaultValues);
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset(defaultValues);
  }, [open, form, defaultValues]);

  const handleSubmit = async (data: Partial<IdentificationDetails>) => {
    if (onSubmit != null) {
      onOpenChange(false);
      onSubmit(data as IdentificationDetails);
      return;
    }
    if (accessionId == null) {
      toast.error("Failed to update identification details");
      throw new Error("Accession ID is required");
    }
    await updateAccession.mutateAsync({
      id: accessionId,
      updates: convertDatesToISO(data),
    });
  };

  const handleRemove = async () => {
    if (onSubmit != null) {
      onOpenChange(false);
      onSubmit(null);
      return;
    }
    if (accessionId == null) {
      toast.error("Failed to remove identification details");
      throw new Error("Accession ID is required");
    }
    await updateAccession.mutateAsync({
      id: accessionId,
      updates: {
        identificationVerificationStatus: null,
        identifiedBy: null,
        identificationQualifier: null,
        identificationRemarks: null,
        dateIdentified: null,
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (
      !open &&
      !discardDialogOpen &&
      Object.keys(form.formState.dirtyFields).length > 0
    ) {
      setDiscardDialogOpen(true);
    } else {
      onOpenChange(open);
      setDiscardDialogOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children}
      <FormProvider {...form}>
        <DialogContent
          asChild
          className="flex max-h-[calc(100%-32px)] max-w-xl flex-col gap-0! bg-grey-25 p-0!"
        >
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
          >
            <DialogHeader className="px-8 py-4">
              <DialogTitle>Identification</DialogTitle>
              <DialogDescription>
                Details about how the material was identified
              </DialogDescription>
              <div className="absolute right-3 top-3 flex items-center">
                {initialData != null && (
                  <Tooltip>
                    <RemoveDialog
                      onDiscard={() => {
                        handleRemove();
                      }}
                      section="identification"
                    >
                      <DialogTrigger
                        asChild
                        onFocusCapture={stopEventPropagation}
                      >
                        <TooltipTrigger asChild>
                          <IconButton
                            icon={Trash3}
                            ariaLabel="Remove identification"
                            variant="tertiaryGray"
                            testId="remove-identification-button"
                          />
                        </TooltipTrigger>
                      </DialogTrigger>
                    </RemoveDialog>
                    <TooltipContent side="bottom" sideOffset={0}>
                      Remove identification
                    </TooltipContent>
                  </Tooltip>
                )}
                <DialogClose asChild>
                  <IconButton
                    icon={XClose}
                    ariaLabel="Close"
                    variant="tertiaryGray"
                  />
                </DialogClose>
              </div>
            </DialogHeader>
            <div className="mx-2 flex flex-col gap-6 overflow-auto rounded-lg border border-grey-200 bg-white px-6 py-6">
              <FormField
                control={form.control}
                name="identifiedBy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Identified by</FormLabel>
                    <FormControl>
                      <Input data-cy="identified-by-input" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dateIdentified"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Date identified</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        data-cy="date-identified-picker"
                      >
                        <Button endIcon={Calendar} className="justify-between">
                          {field.value == null
                            ? "Select a date identified..."
                            : createDateLabel(field.value)}
                        </Button>
                      </DatePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormSelect
                name="identificationVerificationStatus"
                id="identification-verification-status-picker"
                label="Verification status"
                testId="identification-verification-status-picker"
                options={pipe(
                  identificationVerificationStatusMap,
                  D.toPairs,
                  A.map(([value, label]) => ({
                    value,
                    label,
                    supportText: `(${value})`,
                  })),
                )}
                required
              />
              <FormSelect
                name="identificationQualifier"
                id="identification-qualifier-picker"
                label="Identification qualifier"
                testId="identification-qualifier-picker"
                options={pipe(
                  identificationQualifierLabelMap,
                  D.toPairs,
                  A.map(([value, label]) => ({
                    value,
                    label,
                    supportText: `(${value})`,
                  })),
                  A.prepend<SelectOption<string>>({
                    value: null,
                    label: "None",
                  }),
                )}
              />
              <FormField
                control={form.control}
                name="identificationRemarks"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Identification remarks</FormLabel>
                    <FormControl>
                      <Textarea
                        data-cy="identification-remarks-input"
                        {...field}
                        value={field.value ?? undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="px-8 py-4">
              <DiscardDialog
                onDiscard={() => {
                  onOpenChange(false);
                }}
                open={discardDialogOpen}
                onOpenChange={setDiscardDialogOpen}
              >
                <DialogTrigger
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleOpenChange(false);
                  }}
                >
                  <Button
                    variant="tertiaryGray"
                    testId="cancel-identification-button"
                  >
                    Cancel
                  </Button>
                </DialogTrigger>
              </DiscardDialog>
              <Button
                variant="primary"
                type="submit"
                testId="save-identification-button"
                loading={form.formState.isSubmitting}
              >
                {initialData == null ? "Add" : "Update"} identification
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
